import { Card } from "@components/cards/card";
import {
  IRaffleDefaultRewardDTO,
  IRaffleDefaultStatusDTO,
} from "@hooks/dtos/raffles/IRaffleDefaultDTO";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  IRaffleCustomerBasicInfo,
  RaffleStatusTypes,
} from "@hooks/dtos/raffles/IRaffleGeneralDTO";
import { goToHome } from "@hooks/functions/General";
import { RaffleStatusEnum } from "@hooks/enums/raffle";
import { getRaffleStatus } from "@hooks/functions/Raffle/status";
import { DraggableRewardsTabs } from "@components/raffle-creation/rewards";

const getRewardsFingerprint = (rewards: IRaffleDefaultRewardDTO[]) =>
  JSON.stringify(rewards);

export function MyDefaultRaffleRewardCreate() {
  const navigate = useNavigate();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [searchParams] = useSearchParams();
  const key = searchParams.get("key");
  const campaign = searchParams.get("campaign");

  // --- Raffle Default Infos --- //
  const [isEditingReward, setIsEditingReward] = useState<boolean>();
  const [raffle, setRaffle] = useState<IRaffleCustomerBasicInfo>();
  const [rewards, setRewards] = useState<IRaffleDefaultRewardDTO[]>([]);
  const [savedRewardsFingerprint, setSavedRewardsFingerprint] = useState("");
  const [isSavingRewards, setIsSavingRewards] = useState(false);
  const [raffleStatus, setRaffleStatus] = useState<IRaffleDefaultStatusDTO>({
    status: "x",
    text: "Carregando...",
    title: "Carregando...",
    color: "warning",
  });

  const [isLoading, setIsLoading] = useState<{ [x: string]: boolean }>({
    raffle: true,
    rewards: true,
  });

  const loadCustomerOwnRaffle = async () => {
    try {
      setIsLoading((prev) => ({
        ...prev,
        raffle: true,
      }));

      const params = `?key=${key}`;
      const response = await api.get(
        `/customer/raffle/default/my/summary${params}`
      );

      setRaffle(response.data);

      setIsLoading((prev) => ({
        ...prev,
        raffle: false,
      }));
    } catch (error: any) {
      setTimeout(() => {
        goToHome();
      }, 1500);

      pushToastify({
        message:
          error?.response?.data?.message || "Falha ao carregar as informações.",
        type: "error",
        theme: "dark",
      });
    }
  };

  const loadCustomerOwnRaffleRewards = async () => {
    try {
      setIsLoading((prev) => ({
        ...prev,
        rewards: true,
      }));

      const params = `?key=${key}`;
      const response = await api.get(
        `/customer/raffle/default/my/rewards${params}`
      );

      // Garante que rewards seja sempre um array ordenado
      const sortedRewards = (response.data || []).sort(
        (a: IRaffleDefaultRewardDTO, b: IRaffleDefaultRewardDTO) =>
          a.order - b.order
      );
      setRewards(sortedRewards);
      setSavedRewardsFingerprint(getRewardsFingerprint(sortedRewards));

      setIsLoading((prev) => ({
        ...prev,
        rewards: false,
      }));
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          "Falha ao carregar as informações do(s) prêmio(s).",
        type: "error",
        theme: "dark",
      });

      // Define um array vazio em caso de erro
      setRewards([]);
      setIsLoading((prev) => ({
        ...prev,
        rewards: false,
      }));
    }
  };

  // Função para salvar a nova ordem dos prêmios
  const handleSaveRewards = async (): Promise<void> => {
    const rewardsFingerprint = getRewardsFingerprint(rewards);

    if (isSavingRewards || rewardsFingerprint === savedRewardsFingerprint) {
      return;
    }

    try {
      setIsSavingRewards(true);

      const response = await api.put(`/customer/raffle/default/my/rewards/`, {
        key,
        rewards,
      });
      const savedRewards = Array.isArray(response.data)
        ? response.data
        : response.data?.rewards;

      if (!Array.isArray(savedRewards)) {
        throw new Error("A resposta não retornou os prêmios salvos.");
      }

      const sortedRewards = [...savedRewards].sort(
        (a: IRaffleDefaultRewardDTO, b: IRaffleDefaultRewardDTO) =>
          a.order - b.order
      );
      setRewards(sortedRewards);
      setSavedRewardsFingerprint(getRewardsFingerprint(sortedRewards));

      pushToastify({
        message: "Premiação registrada com sucesso!",
        type: "success",
        theme: "dark",
      });
    } catch (error: any) {
      pushToastify({
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Falha ao atualizar a premiação.",
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsSavingRewards(false);
    }
  };

  // Handler para mudanças na lista de prêmios
  const handleRewardsChange = (updatedRewards: IRaffleDefaultRewardDTO[]) => {
    // --- Update the Reward Order --- //
    setRewards(updatedRewards);
  };

  const hasUnsavedRewardChanges =
    getRewardsFingerprint(rewards) !== savedRewardsFingerprint;

  useEffect(() => {
    setRaffleStatus(getRaffleStatus(raffle?.status as RaffleStatusTypes));

    // --- Call to load the Rewards --- //
    loadCustomerOwnRaffleRewards();
  }, [raffle]);

  useEffect(() => {
    loadCustomerOwnRaffle();
  }, []);

  // --- Check if can be created Rewards --- //
  if (!isLoading.raffle) {
    if (raffle && raffle.status !== RaffleStatusEnum.CREATING) {
      setTimeout(() => {
        goToHome();
      }, 2000);

      return (
        <>
          <Card>
            <div className="card-header text-center">
              <div className="card-title">Acesso Negado</div>
            </div>
            <div className="card-body">
              <div className="row mb-3 text-center">
                <div className="h5 fw-bold">Acesso não permitido</div>
                <div className="h6">
                  A premiação desta Campanha não pode mais ser alterada.
                </div>
                <div className="h7">
                  Você será redirecionado em instantes...
                </div>
              </div>
            </div>
          </Card>
        </>
      );
    }
  }

  return (
    <>
      {/* Main Things */}
      <Card>
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                navigate(
                  `/raffle/default/view?campaign=${campaign}${
                    key ? `&key=${key}` : ``
                  }`
                );
              }}
            >
              Voltar
            </button>
            <div className="card-title mb-0">Informações da Campanha</div>
            <button className="btn btn-sm btn-primary invisible">Botão</button>
          </div>
        </div>
        <div className="card-body">
          <div className="row mb-3 text-center">
            <div className="h5 fw-bold">{raffle?.name || `Carregando...`}</div>
            <div className="h6">
              {raffle?.description_short || `Carregando...`}
            </div>
          </div>
        </div>
      </Card>

      {/* Campaign Summary */}
      <Card>
        <div className="card-header">
          <div className="text-center">
            <div className="card-title">Resumo da Campanha</div>
          </div>
        </div>
        <div className="card-body">
          {/* Type & Status */}
          <div className="row">
            <div className="d-flex justify-content-between">
              {/* Campaign Type */}
              <div
                className="col-md-5 mx-auto py-2 text-center border rounded-4 fw-bold"
                style={{ cursor: "pointer" }}
                title={
                  raffle?.typeData?.description_short ||
                  "Erro ao carregar informação."
                }
              >
                <div className="d-flex justify-content-center align-items-center">
                  <span>{raffle?.typeData?.name}</span>
                </div>
              </div>

              {/* Campaign Status */}
              {isLoading.raffle ? (
                <>
                  <div className="col-md-5 mx-auto p-2 text-center border rounded-4">
                    <span>Carregando status da campanha...</span>
                    <Skeleton count={1} />
                  </div>
                </>
              ) : (
                <>
                  <div
                    className="col-md-5 mx-auto p-2 text-center border rounded-4"
                    title={raffleStatus?.title}
                  >
                    <div className="d-flex justify-content-center align-items-center">
                      <span
                        className={`rounded-circle d-inline-block me-2 bg-${raffleStatus?.color}`}
                        style={{ width: 15, height: 15 }}
                        title={raffleStatus?.title}
                      ></span>
                      <span>{raffleStatus?.text}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Rewards */}
      <Card>
        <div className="card-header">
          <div className="text-center">
            <div className="card-title">
              Prêmio{rewards.length > 1 ? `s` : ``} da Campanha
            </div>
            <small className="text-muted">
              Arraste as abas para alterar a ordem dos prêmios
            </small>
          </div>
        </div>
        <div className="card-body">
          {isLoading.rewards ? (
            <div className="text-center">
              <Skeleton count={3} height={60} />
            </div>
          ) : (
            <>
              <DraggableRewardsTabs
                rewards={rewards}
                onRewardsChange={handleRewardsChange}
                onEditingReward={(e) => {
                  setIsEditingReward(e);
                }}
                maxRewards={10}
              />

              {/* Button to Save all Changes */}
              {!isEditingReward && rewards.length > 0 && (
                <div className="text-center mt-4">
                  <button
                    className={`btn btn-${
                      isEditingReward ? `outline-success` : `success`
                    } btn-lg`}
                    disabled={!hasUnsavedRewardChanges || isSavingRewards}
                    title={
                      isSavingRewards
                        ? "Salvando prêmios..."
                        : hasUnsavedRewardChanges
                        ? "Salvar todas as alterações nos prêmios"
                        : "Não há alterações pendentes para salvar"
                    }
                    onClick={() => {
                      if (isEditingReward) {
                        return pushToastify({
                          message:
                            "Você precisa salvar as alterações individuais do prêmio antes de finalizar.",
                          type: "error",
                          theme: "dark",
                        });
                      }
                      void handleSaveRewards();
                    }}
                  >
                    {isSavingRewards
                      ? "Salvando Prêmios..."
                      : "Salvar Todos os Prêmios"}
                  </button>
                  <small className="d-block text-muted mt-2">
                    {hasUnsavedRewardChanges
                      ? "Existem alterações pendentes para salvar"
                      : "Nenhuma alteração pendente"}
                  </small>
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </>
  );
}
